import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  TextField,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  BusinessCenter as BusinessIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { getTownBySlug } from '../data/townData';

const TownDetail: React.FC = () => {
  const { townSlug } = useParams<{ townSlug: string }>();
  const town = townSlug ? getTownBySlug(townSlug) : undefined;

  if (!town) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h2" gutterBottom>Town Not Found</Typography>
        <Typography variant="body1" sx={{ mb: 4 }}>
          The requested town page could not be found.
        </Typography>
        <Button variant="contained" component={Link} to="/locations">
          Return to Locations
        </Button>
      </Container>
    );
  }

  // Schema markup for local business
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "TitleCompany",
    "name": "MAD Title Agency LLC",
    "description": `Professional title services in ${town.name}, ${town.county}, ${town.state}`,
    "url": `https://madtitle.pro/locations/${town.slug}`,
    "telephone": "(732) 874-5425",
    "email": "madtitleagency@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "515 Amboy Avenue",
      "addressLocality": "Woodbridge",
      "addressRegion": "NJ",
      "postalCode": "07095",
      "addressCountry": "US"
    },
    "areaServed": {
      "@type": "City",
      "name": town.name,
      "addressRegion": town.state,
      "addressCountry": "US"
    },
    "serviceType": [
      "Title Insurance",
      "Real Estate Closings",
      "Title Searches",
      "Settlement Services",
      "Escrow Services"
    ],
    "priceRange": "$$",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5.0",
      "reviewCount": "150+"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": town.faq.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>{town.metaTitle}</title>
        <meta name="description" content={town.metaDescription} />
        <meta name="keywords" content={`title company ${town.name} NJ, real estate closing ${town.name}, title insurance ${town.county}, ${town.name} title services`} />
        <meta property="og:title" content={town.metaTitle} />
        <meta property="og:description" content={town.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://madtitle.pro/locations/${town.slug}`} />
        <link rel="canonical" href={`https://madtitle.pro/locations/${town.slug}`} />
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      {/* Breadcrumb Navigation */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            <Link to="/" style={{ textDecoration: 'none', color: '#1e3a8a' }}>Home</Link>
            {' > '}
            <Link to="/locations" style={{ textDecoration: 'none', color: '#1e3a8a' }}>Locations</Link>
            {' > '}
            <span>{town.name}</span>
          </Typography>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  lineHeight: 1.2,
                }}
              >
                Title Services in {town.name}, {town.state}
              </Typography>
              <Typography variant="h5" sx={{ mb: 3, opacity: 0.9 }}>
                Your trusted title partner in {town.county}
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', opacity: 0.8 }}>
                {town.description}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  href="tel:(732) 874-5425"
                  sx={{
                    backgroundColor: '#f59e0b',
                    color: '#1e3a8a',
                    fontWeight: 700,
                    px: 4,
                    py: 1.5,
                    '&:hover': { backgroundColor: '#d97706' },
                  }}
                  startIcon={<PhoneIcon />}
                >
                  Call Now: (732) 874-5425
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  href="#quote-form"
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  Get Free Quote
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  color: '#1e3a8a',
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  {town.name} Market Overview
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>Population</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>{town.population}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>Typical Home Value</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>{town.medianHomePrice}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>Market Growth</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#059669' }}>
                    {town.marketTrend}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {town.zipCodes.map((zip, index) => (
                    <Chip key={index} label={zip} size="small" variant="outlined" />
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Why Choose MAD Title in This Town */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              color: '#1f2937',
              mb: 1,
            }}
          >
            Why Choose MAD Title in {town.name}?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              color: '#4b5563',
              mb: 6,
              maxWidth: 600,
              mx: 'auto',
              fontSize: '1.1rem',
            }}
          >
            Local expertise meets professional excellence for your {town.name} real estate transaction
          </Typography>

          <Grid container spacing={4}>
            {town.serviceHighlights.map((highlight, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    textAlign: 'center',
                    p: 3,
                    height: '100%',
                    border: '1px solid #e2e8f0',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(30, 41, 59, 0.12)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  <Box sx={{ mb: 2 }}>
                    {index === 0 && <SecurityIcon sx={{ fontSize: 48, color: '#f59e0b' }} />}
                    {index === 1 && <SpeedIcon sx={{ fontSize: 48, color: '#f59e0b' }} />}
                    {index === 2 && <BusinessIcon sx={{ fontSize: 48, color: '#f59e0b' }} />}
                    {index === 3 && <CheckCircleIcon sx={{ fontSize: 48, color: '#f59e0b' }} />}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {highlight}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Specialized service tailored to {town.name} market conditions
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Local Market Insights */}
        <Box sx={{ mb: 8 }}>
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ fontWeight: 600, color: '#1f2937', mb: 3 }}>
                {town.name} Neighborhoods We Serve
              </Typography>
              <List>
                {town.keyNeighborhoods.map((neighborhood, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <LocationIcon sx={{ color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={neighborhood}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ fontWeight: 600, color: '#1f2937', mb: 3 }}>
                Notable Features
              </Typography>
              <List>
                {town.notableFeatures.map((feature, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <StarIcon sx={{ color: '#f59e0b' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={feature}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </Box>

        {/* Client Testimonial */}
        <Box sx={{ mb: 8 }}>
          <Paper
            elevation={3}
            sx={{
              p: 6,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              border: '1px solid #e2e8f0',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontStyle: 'italic',
                color: '#374151',
                mb: 3,
                lineHeight: 1.6,
                fontWeight: 500,
              }}
            >
              "{town.testimonial.quote}"
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 1 }}>
              {town.testimonial.client}
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', fontWeight: 500 }}>
              {town.testimonial.propertyType} in {town.name}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              {[...Array(5)].map((_, i) => (
                <StarIcon key={i} sx={{ fontSize: 20, color: '#f59e0b' }} />
              ))}
            </Box>
          </Paper>
        </Box>

        {/* FAQ Section */}
        <Box sx={{ mb: 8 }}>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              color: '#1f2937',
              mb: 1,
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              color: '#4b5563',
              mb: 4,
              maxWidth: 600,
              mx: 'auto',
              fontSize: '1.1rem',
            }}
          >
            Common questions about title services in {town.name}
          </Typography>

          {town.faq.map((item, index) => (
            <Accordion
              key={index}
              sx={{
                mb: 1,
                '&:before': { display: 'none' },
                border: '1px solid #e2e8f0',
                borderRadius: 1,
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                  {item.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.7, fontSize: '1rem' }}>
                  {item.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Quote Request Form */}
        <Box id="quote-form" sx={{ mb: 8 }}>
          <Paper
            elevation={3}
            sx={{
              p: 6,
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
              color: 'white',
            }}
          >
            <Typography
              variant="h3"
              component="h2"
              sx={{
                textAlign: 'center',
                fontWeight: 600,
                mb: 1,
              }}
            >
              Get Your {town.name} Title Quote
            </Typography>
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                mb: 4,
                opacity: 0.95,
                maxWidth: 600,
                mx: 'auto',
                fontSize: '1.1rem',
              }}
            >
              Ready to move forward with your {town.name} property? Get a personalized quote for your title services.
            </Typography>

            <Grid container spacing={3} maxWidth="md" sx={{ mx: 'auto' }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Property Address"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Additional Details"
                  multiline
                  rows={3}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'white',
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: '#f59e0b',
                    color: '#1f2937',
                    fontWeight: 700,
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    '&:hover': {
                      backgroundColor: '#d97706',
                    },
                  }}
                >
                  Get My Free Quote
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Contact Information */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1f2937', mb: 3 }}>
            Contact Our {town.name} Title Experts
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} sm={4}>
              <Card sx={{ p: 3, textAlign: 'center' }}>
                <PhoneIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Call Us
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  (732) 874-5425
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ p: 3, textAlign: 'center' }}>
                <EmailIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Email Us
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  madtitleagency@gmail.com
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ p: 3, textAlign: 'center' }}>
                <LocationIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Visit Us
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  515 Amboy Avenue<br />Woodbridge, NJ 07095
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default TownDetail; 